/**
 * NBPLCDashboard — Nigeria Breweries Corporate Dashboard
 * Route: /corporate/nbplc/dashboard
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useNBPLC } from "@/context/NBPLCContext";
import { PageHeader, SectionCard, MetricCard } from "@/components/ds";
import { RefreshCw } from "lucide-react";
import client from "@/api/client";

export default function NBPLCDashboard() {
  const navigate = useNavigate();
  const { selectedPeriod, period, periods, setSelectedPeriod, evidencePct } = useNBPLC();
  const [dash, setDash] = useState(null);
  const [loading, setLoading] = useState(true);

  // Shared confidence helper
  const confidence = evidencePct >= 70
    ? { label: "High Confidence",   cls: "bg-emerald-100 text-emerald-700 border-emerald-200", warning: null }
    : evidencePct >= 30
    ? { label: "Medium Confidence", cls: "bg-amber-100 text-amber-700 border-amber-200",   warning: "Moderate confidence — some evidence missing" }
    : { label: "Low Confidence",    cls: "bg-red-100 text-red-700 border-red-200",          warning: "Based on estimated data — requires validation" };

  useEffect(() => {
    if (!selectedPeriod) return;
    setLoading(true);
    client.get(`/nbplc/dashboard?period_id=${selectedPeriod}`)
      .then(r => { setDash(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [selectedPeriod]);

  if (loading && !dash) return (
    <div className="p-6 space-y-4">{[1,2,3].map(i=><div key={i} className="h-14 bg-slate-100 rounded-xl animate-pulse"/>)}</div>
  );

  const t = dash?.totals || {};
  const opps = dash?.opportunities || {};
  const comp = dash?.data_completeness_pct || 0;

  return (
    <div className="p-6 lg:p-8 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-0.5">Nigeria Breweries Plc</p>
          <PageHeader title="Corporate Climate Dashboard" />
          <p className="text-xs text-slate-400 mt-0.5">
            Net Zero 2030 · {period?.label} · DCI-BREWERY-V1.0
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select value={selectedPeriod||""} onChange={e=>setSelectedPeriod(e.target.value)}
            className="text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 bg-white text-slate-700">
            {periods.map(p=><option key={p.period_id} value={p.period_id}>{p.label}</option>)}
          </select>
          <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${comp>=100?"bg-teal-100 text-teal-700 border-teal-200":"bg-amber-100 text-amber-700 border-amber-200"}`}>
            {comp}% complete
          </span>
        </div>
      </div>

      {/* KPI Strip */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="nb-kpi-strip">
        <MetricCard label="Total Emissions"   value={`${Number((t.total||0)/1000).toFixed(0)}K`} unit="tCO₂e/month" color="text-emerald-700" />
        <MetricCard label="Scope 1 (Fuel)"    value={`${Number((t.scope1||0)/1000).toFixed(0)}K`} unit="tCO₂e" />
        <MetricCard label="Scope 2 (Grid)"    value={`${Number((t.scope2||0)/1000).toFixed(0)}K`} unit="tCO₂e" color="text-blue-700" />
        <MetricCard label="Scope 3 (Pkg+Dist)" value={`${Number((t.scope3||0)/1000).toFixed(0)}K`} unit="tCO₂e" color="text-slate-500" />
        <MetricCard label="Renewable Energy"  value={`${t.renewable_pct||0}%`} color={t.renewable_pct>15?"text-teal-700":"text-amber-600"} />
        <MetricCard label="VRS Score"         value={dash?.vrs_score||0} unit="/100" color={dash?.vrs_score>=80?"text-teal-700":"text-amber-600"}
          sub={dash?.evidence_completeness_pct !== undefined ? `Evidence: ${dash.evidence_completeness_pct}%` : undefined}
          data-testid="nb-vrs-score" />
      </div>

      {/* Confidence Level banner */}
      <div className={`flex items-center justify-between px-4 py-2.5 rounded-xl border ${confidence.cls}`} data-testid="nb-confidence-banner">
        <div className="flex items-center gap-2">
          <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full border ${confidence.cls}`}>{confidence.label}</span>
          <span className="text-xs font-semibold">
            {confidence.warning || "Evidence complete — outputs are high confidence"}
          </span>
        </div>
        <span className="text-[10px] text-slate-500">Evidence: {evidencePct}% · VRS: {dash?.vrs_score||0}/100</span>
      </div>

      {/* Main grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* By plant */}
        <div className="lg:col-span-2">
          <SectionCard>
            <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-3">Emissions by Brewery Plant</p>
            {(dash?.by_bu||[]).map((bu,i) => {
              const pct = t.total > 0 ? (bu.total / t.total * 100) : 0;
              const colors = ["#059669","#0284c7","#f59e0b","#8b5cf6","#ef4444"];
              return (
                <div key={bu.bu_id} className="mb-3">
                  <div className="flex justify-between text-xs mb-1">
                    <span className="font-semibold text-slate-700">{bu.bu_name.replace("NB ","")}</span>
                    <span className="text-slate-500 tabular-nums">{Number(bu.total).toLocaleString()} tCO₂e · {pct.toFixed(1)}%</span>
                  </div>
                  <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all" style={{width:`${pct}%`, background: colors[i%5]}}/>
                  </div>
                </div>
              );
            })}
          </SectionCard>
        </div>

        {/* Opportunities */}
        <SectionCard>
          <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-3">Carbon Reduction Pipeline</p>
          <div className="space-y-2">
            {[
              ["Opportunities identified", opps.count||0, "text-teal-700"],
              ["Reduction potential", `${Number((opps.total_reduction_tco2e_yr||0)/1000).toFixed(0)}K tCO₂e/yr`, "text-emerald-700"],
              ["Indicative carbon value", `$${Number((opps.carbon_revenue_usd_yr?.base_25||0)/1e6).toFixed(1)}M/yr (cond.)`, "text-blue-700"],
            ].map(([l,v,c]) => (
              <div key={l} className="flex justify-between py-1.5 border-b border-slate-50 text-xs last:border-0">
                <span className="text-slate-500">{l}</span>
                <span className={`font-bold ${c}`}>{v}</span>
              </div>
            ))}
          </div>
          <button onClick={() => navigate("/corporate/nbplc/value-bridge")}
            className="w-full mt-4 py-2 bg-[#0F172A] text-white text-xs font-bold rounded-xl hover:bg-slate-800">
            View Value Bridge →
          </button>
          <p className="text-[9px] text-slate-400 italic mt-1.5 text-center">
            Financial outputs derived from modeled scenarios
          </p>
        </SectionCard>
      </div>

      {/* NB Sustainability Narrative */}
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-5 py-4">
        <p className="text-xs font-bold text-emerald-800 mb-1">Net Zero 2030 — Nigeria Breweries Plc</p>
        <p className="text-xs text-emerald-700">
          NB targets net zero production emissions by 2030 aligned with Science Based Targets. 
          Key levers: Solar PV rollout ({t.renewable_pct||0}% renewable today → 50% target), 
          biomass thermal transition, returnable bottle programme scale-up, and water intensity reduction. 
          This platform tracks the full pipeline from monthly measurement to carbon value and financing.
        </p>
      </div>
    </div>
  );
}
